import {injectable} from 'inversify'
import {action, computed, makeObservable, observable, reaction, runInAction, toJS} from 'mobx'
import {message} from 'antd'
import moment from 'moment/moment'
import {ChartOptions} from 'chart.js'
import i18next from 'i18next'
import {
    AuthStoreInterface,
    ECOMTransactionSummaryResponse,
    generateChartTitleForRange,
    generateInterval,
    getECOMTransactionsSummary,
    getIntervalValue,
    getPOSTransactionsSummaryV2
} from '@/stores'
import {TTransactionsCardWithBarChartContainerStore} from '@/components'
import {LoadingState} from '@/types'
import {
    Currencies,
    PaymentsStatus,
    PaymentsType,
    POSPaymentStatus,
    OnlinePaymentStatus,
    DATE_FORMAT,
    DATE_TIME_FORMAT
} from '@/constants'
import {barChartConfig} from '@/services/chart-config'
import {emptyChartDataGenerator} from '@/stores/reports/services/mocks'
import {IPaymentsTransactionsFilterStore} from '~/code/pages/Overview/components/OveriviewPaymentsFilter'
import {hasPermissions} from '@/stores/auth/services'
import {PermissionsMap} from '@/stores/auth/constants/permissions-map'

@injectable()
export class PaymentsTransactionsCardWithBarChartContainerStore
    implements TTransactionsCardWithBarChartContainerStore
{
    authStore: AuthStoreInterface
    paymentsTransactionsFilterStore: IPaymentsTransactionsFilterStore
    constructor(
        authStore: AuthStoreInterface,
        paymentsTransactionsFilterStore: IPaymentsTransactionsFilterStore
    ) {
        makeObservable(this, {
            dataLoadingState: observable,
            currency: computed,
            selectedDataType: observable,
            data: computed,
            dateRange: computed,
            interval: computed,
            setSelectedDataType: action
        })

        this.authStore = authStore
        this.paymentsTransactionsFilterStore = paymentsTransactionsFilterStore

        this.setupFilterReaction()

        this.loadData()
    }

    dataLoadingState = LoadingState.IDLE
    _data = {amount: undefined, count: undefined}
    selectedDataType = this.dataTypes[0]

    // AbortController for in-flight requests — aborted when a newer request starts
    private _abortController: AbortController | null = null

    get data() {
        if (this.shouldLoadData()) {
            this.loadData()
        }

        if (!this._data?.amount) {
            return this.getEmptyChartData()
        }

        const currentData = this._data[this.selectedDataType.key]

        return {
            data: currentData,
            options: barChartConfig.barPaymentsCustomTooltipOptions(
                currentData.datasets.length,
                this.selectedDataType.key
            )
        }
    }

    get dataTypes(): {key: string; title: string}[] {
        return [
            {key: 'amount', title: i18next.t('globals:fields.amount')},
            {key: 'count', title: i18next.t('globals:fields.number')}
        ]
    }

    get currency() {
        return this.paymentsTransactionsFilterStore.currency as Currencies
    }

    get dateRange() {
        return {
            from: this.paymentsTransactionsFilterStore.startDate,
            to: this.paymentsTransactionsFilterStore.endDate
        }
    }

    get interval() {
        return {
            from: this.paymentsTransactionsFilterStore.startDate,
            to: this.paymentsTransactionsFilterStore.endDate
        }
    }

    get selectedStatus() {
        return this.paymentsTransactionsFilterStore.status as PaymentsStatus
    }

    get statuses() {
        return this.paymentsTransactionsFilterStore.statuses
    }

    setData = (transactions: ECOMTransactionSummaryResponse) => {
        const labels = this.generateLabels(transactions)

        this._data = {
            amount: this.createDataset(labels, transactions, 'amount'),
            count: this.createDataset(labels, transactions, 'count')
        }
    }

    private createDataset(
        labels: string[],
        transactions: ECOMTransactionSummaryResponse,
        type: 'amount' | 'count'
    ) {
        return {
            labels,
            datasets: [
                {
                    label: this.currency,
                    ...(type === 'amount' && {currency: this.currency}),
                    data: transactions?.map((item) => this.mapTransactionToDataPoint(item, type))
                }
            ]
        }
    }

    private mapTransactionToDataPoint(item: any, type: 'amount' | 'count') {
        const value = item[type]
        return {
            x: this.generateLabel(item),
            // Use null for zero values so Chart.js won't render a bar (minBarLength won't apply)
            // Non-zero values will use the minBarLength: 5 setting
            y: value === 0 ? null : value,
            full: {
                date: item.date,
                amount: item.amount,
                count: item.count,
                status: this.selectedStatus,
                currency: this.currency
            }
        }
    }

    setSelectedStatus = (status: string) => {
        this.paymentsTransactionsFilterStore.setStatus(status)
    }

    setSelectedDataType = (dataTypeKey: string) => {
        this.selectedDataType = this.dataTypes.find((item) => item.key === dataTypeKey)
    }

    /**
     * Merges ECOM and POS transaction data by date, summing amounts and counts.
     * Only merges transactions with matching currencies.
     * Uses the unified selectedStatus for consistent status representation.
     */
    private mergeTransactionsByDate(
        ecomData: ECOMTransactionSummaryResponse,
        posData: ECOMTransactionSummaryResponse
    ): ECOMTransactionSummaryResponse {
        const mergedMap = new Map()
        const targetCurrency = this.currency
        const unifiedStatus = this.selectedStatus

        // Add ECOM data to map (accumulate hourly entries into daily buckets)
        ecomData.forEach((item) => {
            const dateKey = moment(item.date).format(DATE_TIME_FORMAT)
            const existing = mergedMap.get(dateKey)

            if (existing) {
                existing.amount += item.amount ?? 0
                existing.count += item.count ?? 0
            } else {
                mergedMap.set(dateKey, {
                    date: item.date,
                    amount: item.amount ?? 0,
                    count: item.count ?? 0,
                    currency: targetCurrency,
                    status: unifiedStatus
                })
            }
        })

        // Merge POS data (accumulate hourly entries into daily buckets)
        posData.forEach((item) => {
            const dateKey = moment(item.date).format(DATE_TIME_FORMAT)
            const existing = mergedMap.get(dateKey)

            if (existing) {
                existing.amount += item.amount ?? 0
                existing.count += item.count ?? 0
            } else {
                mergedMap.set(dateKey, {
                    date: item.date,
                    amount: item.amount ?? 0,
                    count: item.count ?? 0,
                    currency: targetCurrency,
                    status: unifiedStatus
                })
            }
        })

        // Convert map to array and sort by date
        return Array.from(mergedMap.values())
            .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())
    }

    async loadData() {
        // Abort any in-flight request before starting a new one
        this._abortController?.abort()
        this._abortController = new AbortController()
        const signal = this._abortController.signal

        this.setLoadingState(LoadingState.LOADING)

        const request = this.buildRequest()
        const paymentsType = this.paymentsTransactionsFilterStore.paymentsType

        try {
            const data = await this.fetchDataByType(paymentsType as PaymentsType, request, signal)
            runInAction(() => this.setData(data))
            this.setLoadingState(LoadingState.DONE)
        } catch (error) {
            // Silently ignore aborted requests — a newer request is already in flight
            if (error?.name === 'AbortError') return

            this.setLoadingState(LoadingState.FAILED)
            message.error(error.message)
        }
    }

    // Helper Methods

    private setupFilterReaction() {
        reaction(
            () => ({
                from: this.paymentsTransactionsFilterStore.startDate,
                to: this.paymentsTransactionsFilterStore.endDate,
                currency: this.paymentsTransactionsFilterStore.currency,
                paymentsType: this.paymentsTransactionsFilterStore.paymentsType,
                transactionStore: this.paymentsTransactionsFilterStore.transactionStore,
                status: this.paymentsTransactionsFilterStore.status,
                ecomStores: this.paymentsTransactionsFilterStore.ecomStores,
                posStores: this.paymentsTransactionsFilterStore.posStores
            }),
            ({from, to, currency, paymentsType, status, ecomStores, posStores}) => {
                if (
                    from &&
                    to &&
                    currency &&
                    this.authStore.isAuthenticated &&
                    paymentsType &&
                    status &&
                    ecomStores !== undefined &&
                    posStores !== undefined
                ) {
                    this.loadData()
                }
            }
        )
    }

    private shouldLoadData(): boolean {
        return this.dataLoadingState === LoadingState.IDLE
    }

    private getEmptyChartData() {
        const options = barChartConfig.barOptions({
            title: generateChartTitleForRange({
                from: this.paymentsTransactionsFilterStore.startDate,
                to: this.paymentsTransactionsFilterStore.endDate
            }),
            showTooltip: true
        }) as ChartOptions

        return {
            data: emptyChartDataGenerator(this.dateRange, generateInterval(this.interval)),
            options
        }
    }

    private generateLabels(transactions?: ECOMTransactionSummaryResponse): string[] {
        return (transactions ?? []).map((item) =>
            getIntervalValue({
                interval: generateInterval(this.interval),
                date: moment(item.date),
                dayFormat: 'DD MMM'
            })
        )
    }

    private generateLabel(item: any): string {
        return getIntervalValue({
            interval: generateInterval(this.interval),
            date: moment(item.date),
            dayFormat: 'DD MMM'
        })
    }

    private buildRequest() {
        const from = this.paymentsTransactionsFilterStore.startDate
        const to = this.paymentsTransactionsFilterStore.endDate

        return {
            from: from.format(),
            to: to.format(),
            currency: this.currency,
            interval: generateInterval({from, to})
        }
    }

    private setLoadingState(state: LoadingState) {
        runInAction(() => {
            this.dataLoadingState = state
        })
    }

    private get hasPosPermissions() {
        return hasPermissions([PermissionsMap.pos_payments.read, PermissionsMap.pos_payments.full])
    }

    private get hasEcomPermissions() {
        return hasPermissions([
            PermissionsMap.online_payments.read,
            PermissionsMap.online_payments.full
        ])
    }

    private mappedStatus(status: PaymentsStatus) {
        switch (status) {
            case PaymentsStatus.charged:
                return {
                    ecom: OnlinePaymentStatus.charge,
                    pos: POSPaymentStatus.charged
                }
            case PaymentsStatus.refunds:
                return {
                    ecom: OnlinePaymentStatus.refund,
                    pos: POSPaymentStatus.refunded
                }
            case PaymentsStatus.pending:
                return {
                    ecom: OnlinePaymentStatus.auth,
                    pos: POSPaymentStatus.authorised
                }
            default:
                return {
                    ecom: undefined,
                    pos: undefined
                }
        }
    }

    private async fetchDataByType(
        paymentsType: PaymentsType,
        request: any,
        signal: AbortSignal
    ): Promise<ECOMTransactionSummaryResponse> {
        switch (paymentsType) {
            case PaymentsType.all:
                return this.fetchAllPaymentsData(request, signal)
            case PaymentsType.ecom:
                if (!this.hasEcomPermissions) {
                    return [] // Return empty data if no permission
                }
                const ecomRequest = {
                    ...request,
                    storeId: this.paymentsTransactionsFilterStore.ecomStoreValue
                }
                return this.fetchECOMData(ecomRequest)
            case PaymentsType.pos:
                if (!this.hasPosPermissions) {
                    return [] // Return empty data if no permission
                }
                const posRequest = {
                    ...request,
                    ...this.paymentsTransactionsFilterStore.posStoreValue
                }
                return this.fetchPOSData(posRequest, signal)
            default:
                throw new Error(`Unknown payment type: ${paymentsType}`)
        }
    }

    private get hasEcomStores(): boolean {
        return this.paymentsTransactionsFilterStore.ecomStores != null && (this.paymentsTransactionsFilterStore.ecomStores?.length ?? 0) > 0
    }

    private get hasPosStores(): boolean {
        return this.paymentsTransactionsFilterStore.posStores != null && (this.paymentsTransactionsFilterStore.posStores?.length ?? 0) > 0
    }

    private async fetchAllPaymentsData(request: any, signal: AbortSignal): Promise<ECOMTransactionSummaryResponse> {
        const canFetchEcom = this.hasEcomStores && this.hasEcomPermissions
        const canFetchPos = this.hasPosStores && this.hasPosPermissions

        // No stores or permissions available
        if (!canFetchEcom && !canFetchPos) {
            return []
        }

        // Only POS stores available
        if (!this.hasEcomStores && canFetchPos) {
            return this.fetchPOSData(request, signal)
        }

        // Only ECOM stores available
        if (!this.hasPosStores && canFetchEcom) {
            return this.fetchECOMData(request)
        }

        // Both store types available - merge data
        if (canFetchEcom && canFetchPos) {
            return this.fetchMergedData(request, signal)
        }

        // Fallback: fetch whichever store type is available
        return canFetchEcom ? this.fetchECOMData(request) : this.fetchPOSData(request, signal)
    }

    private async fetchMergedData(request: any, signal: AbortSignal): Promise<ECOMTransactionSummaryResponse> {
        const ecomStatus = this.mappedStatus(this.selectedStatus).ecom
        const posStatus = this.mappedStatus(this.selectedStatus).pos

        const ecomRequest = {
            ...request,
            status: ecomStatus === OnlinePaymentStatus.refund ? `${OnlinePaymentStatus.refund},${OnlinePaymentStatus.credited}` : ecomStatus
        }

        const posRequest = {
            ...request,
            ...this.paymentsTransactionsFilterStore.posStoreValue,
            status: posStatus
        }

        const [ecom, pos] = await Promise.all([
            getECOMTransactionsSummary(ecomRequest),
            getPOSTransactionsSummaryV2(posRequest, signal)
        ])

        if (ecom.error)
            throw new Error(
                ecom.error.message ||
                    i18next.t('PaymentsTransactionSummary.errors.failedToFetchEcomSummary')
            )
        if (pos.error)
            throw new Error(
                pos.error.message ||
                    i18next.t('PaymentsTransactionSummary.errors.failedToFetchPosSummary')
            )

        return this.mergeTransactionsByDate(ecom.result, pos.result.data)
    }

    private async fetchECOMData(request: any): Promise<ECOMTransactionSummaryResponse> {
        const status = this.mappedStatus(this.selectedStatus).ecom

        const ecomRequest = {
            ...request,
            status: status === OnlinePaymentStatus.refund ? `${OnlinePaymentStatus.refund},${OnlinePaymentStatus.credited}` : status
        }

        const {result, error} = await getECOMTransactionsSummary(ecomRequest)
        if (error)
            throw new Error(
                error.message ||
                    i18next.t('PaymentsTransactionSummary.errors.failedToFetchEcomSummary')
            )

        return result
    }

    private async fetchPOSData(request: any, signal?: AbortSignal): Promise<ECOMTransactionSummaryResponse> {
        const status = this.mappedStatus(this.selectedStatus).pos

        const posRequest = {
            ...request,
            ...this.paymentsTransactionsFilterStore.posStoreValue,
            status
        }

        const {result, error} = await getPOSTransactionsSummaryV2(posRequest, signal)
        if (error)
            throw new Error(
                error.message ||
                    i18next.t('PaymentsTransactionSummary.errors.failedToFetchPosSummary')
            )

        return result.data
    }
}
